// NOTICE: KAPTION-INTEGRATION
// sales-pipeline — pure helpers for the configurable Sales Pipeline board.
// Kept framework-free so the board rules (stage resolution, filter payloads,
// swimlanes, WIP) are unit-tested without mounting components.

export const UNSTAGED_ID = 'unstaged';
export const UNSTAGED_COLOR = '#94A3B8';
export const DEFAULT_STATUSES = ['open', 'pending', 'snoozed'];
export const ALL_STATUSES = ['open', 'pending', 'snoozed', 'resolved'];
export const GROUP_BY = { NONE: 'none', ASSIGNEE: 'assignee', TEAM: 'team' };
export const NO_LANE_ID = 'none';

const lower = value => String(value || '').toLowerCase();

/**
 * Column descriptors for a pipeline, in board order: the optional unstaged
 * column first, then the admin's order with terminal (Won/Lost) stages moved
 * to the right edge (they render as collapsible strips there).
 */
export const buildStages = pipeline => {
  if (!pipeline) return [];
  const stages = (pipeline.stages || []).map(stage => ({
    id: String(stage.label_id),
    labelId: stage.label_id,
    labelTitle: stage.label_title,
    name: stage.name || stage.label_title,
    color: stage.color || UNSTAGED_COLOR,
    terminal: stage.terminal || null,
    wipLimit: stage.wip_limit || null,
    resolveOnEnter: !!stage.resolve_on_enter,
    isUnstaged: false,
  }));
  const open = stages.filter(s => !s.terminal);
  const terminal = stages.filter(s => s.terminal);
  const unstaged = pipeline.unstaged_column?.enabled
    ? [
        {
          id: UNSTAGED_ID,
          labelId: null,
          labelTitle: null,
          name: pipeline.unstaged_column.name,
          color: UNSTAGED_COLOR,
          terminal: null,
          wipLimit: null,
          resolveOnEnter: false,
          isUnstaged: true,
        },
      ]
    : [];
  return [...unstaged, ...open, ...terminal];
};

export const stageLabelTitles = stages =>
  stages.filter(s => !s.isUnstaged).map(s => s.labelTitle);

/** The stage id a conversation sits in, or null when it is not on the board. */
export const stageIdOf = (conversation, stages) => {
  const labels = (conversation?.labels || []).map(lower);
  const match = stages.find(
    s => !s.isUnstaged && labels.includes(lower(s.labelTitle))
  );
  if (match) return match.id;
  return stages.some(s => s.isUnstaged) ? UNSTAGED_ID : null;
};

/** Labels on the card that are NOT pipeline stage labels. */
export const nonStageLabels = (conversation, stages) => {
  const stageTitles = stageLabelTitles(stages).map(lower);
  return (conversation?.labels || []).filter(
    l => !stageTitles.includes(lower(l))
  );
};

/**
 * Terminal (Won/Lost) columns ignore the status filter: moving a deal there can
 * resolve the conversation, and it must not vanish from its own column.
 */
export const statusesFor = (stage, filters) =>
  stage?.terminal ? [] : filters.statuses || [];

const intersectOrSelf = (scoped, chosen) => {
  if (!scoped?.length) return chosen || [];
  if (!chosen?.length) return scoped;
  const allowed = chosen.filter(id => scoped.includes(id));
  // Empty intersection must still match nothing, not everything.
  return allowed.length ? allowed : [-1];
};

const stripOperator = ({ query_operator: _op, ...condition }) => condition;
// The filter endpoint joins conditions left to right without parentheses, so
// only AND-only payloads can be appended to ours; anything with OR is applied
// on the client with the list's own matcher.
const withAnd = conditions =>
  conditions.map((c, i) => ({
    ...c,
    query_operator: i < conditions.length - 1 ? 'and' : undefined,
  }));
const isAndOnly = payload =>
  payload
    .slice(0, -1)
    .every(c => !c.query_operator || c.query_operator === 'and');

export const emptyFilters = () => ({
  inboxIds: [],
  teamIds: [],
  assigneeIds: [],
  unassigned: false,
  labels: [],
  statuses: [],
  extra: [],
  clientPayloads: [],
  unread: false,
  unattended: false,
  unsupported: null,
});

/**
 * sales-pipeline.HEADER.2 — translate the chat list's selection (view switcher,
 * quick-filter chips, status menu, folder / advanced filters) into board filters.
 */
export const listScopeToFilters = (scope = {}) => {
  const filters = emptyFilters();
  if (scope.inboxId) filters.inboxIds = [scope.inboxId];
  if (scope.teamId) filters.teamIds = [scope.teamId];
  if (scope.label) filters.labels = [scope.label];
  if (scope.status && scope.status !== 'all') filters.statuses = [scope.status];

  if (scope.assigneeId) {
    filters.assigneeIds = [scope.assigneeId];
  } else if (scope.assistantId) {
    filters.assigneeIds = [scope.assistantId];
  } else if (scope.conversationType === 'kaption_assistants') {
    filters.assigneeIds = scope.assistantIds?.length
      ? scope.assistantIds
      : [-1];
  }
  if (!scope.assigneeId) {
    if (scope.assigneeTab === 'me' && scope.currentUserId) {
      filters.assigneeIds = [scope.currentUserId];
    } else if (scope.assigneeTab === 'unassigned') {
      filters.unassigned = true;
    }
  }
  filters.unread =
    scope.assigneeTab === 'unread' || scope.conversationType === 'unread';
  filters.unattended = scope.conversationType === 'unattended';
  if (scope.conversationType === 'mention') filters.unsupported = 'mention';

  [scope.folderPayload, scope.appliedFilters].forEach(payload => {
    if (!payload?.length) return;
    if (isAndOnly(payload)) filters.extra.push(...payload.map(stripOperator));
    else filters.clientPayloads.push(payload);
  });
  return filters;
};

/**
 * /conversations/filter payload for one column: stage condition + pipeline
 * scope (inboxes, teams) + the chat list's selection.
 */
export const buildFilterPayload = ({ stage, stages, scope = {}, filters }) => {
  const conditions = [];
  if (stage.isUnstaged) {
    const titles = stageLabelTitles(stages);
    if (titles.length) {
      conditions.push({
        attribute_key: 'labels',
        filter_operator: 'not_equal_to',
        values: titles,
      });
    }
  } else {
    conditions.push({
      attribute_key: 'labels',
      filter_operator: 'equal_to',
      values: [stage.labelTitle],
    });
  }
  (filters.labels || []).forEach(label => {
    conditions.push({
      attribute_key: 'labels',
      filter_operator: 'equal_to',
      values: [label],
    });
  });

  const inboxIds = intersectOrSelf(scope.inbox_ids, filters.inboxIds);
  if (inboxIds.length) {
    conditions.push({
      attribute_key: 'inbox_id',
      filter_operator: 'equal_to',
      values: inboxIds,
    });
  }
  const teamIds = intersectOrSelf(scope.team_ids, filters.teamIds);
  if (teamIds.length) {
    conditions.push({
      attribute_key: 'team_id',
      filter_operator: 'equal_to',
      values: teamIds,
    });
  }
  if (filters.assigneeIds?.length) {
    conditions.push({
      attribute_key: 'assignee_id',
      filter_operator: 'equal_to',
      values: filters.assigneeIds,
    });
  } else if (filters.unassigned) {
    conditions.push({
      attribute_key: 'assignee_id',
      filter_operator: 'is_not_present',
      values: [],
    });
  }
  const statuses = statusesFor(stage, filters);
  if (statuses.length) {
    conditions.push({
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: statuses,
    });
  }
  conditions.push(...(filters.extra || []));

  return conditions.map((condition, index) => ({
    attribute_model: 'standard',
    ...condition,
    query_operator: index < conditions.length - 1 ? 'and' : undefined,
  }));
};

// meta.* wins even when null (unassigned) — a stale top-level id must not leak back.
const metaIdOf = (c, key, fallbackKey) => {
  if (c?.meta && key in c.meta) return c.meta[key]?.id ?? null;
  return c?.[fallbackKey] ?? null;
};
const teamIdOf = c => metaIdOf(c, 'team', 'team_id');
const assigneeIdOf = c => metaIdOf(c, 'assignee', 'assignee_id');

/**
 * Filters the server cannot apply (unread, unattended, OR folders) — checked
 * on the client for every card, loaded or live.
 */
export const matchesClientFilters = (conversation, filters, matcher) => {
  if (filters.unread && !(conversation.unread_count > 0)) return false;
  if (filters.unattended && !Number(conversation.waiting_since)) return false;
  return (filters.clientPayloads || []).every(
    payload => !matcher || matcher(conversation, payload)
  );
};

/** Does a (live-updated) conversation belong on the board in this stage? */
export const matchesBoard = (
  conversation,
  stage,
  { scope = {}, filters },
  matcher = null
) => {
  if (!stage) return false;
  const inboxIds = intersectOrSelf(scope.inbox_ids, filters.inboxIds);
  if (inboxIds.length && !inboxIds.includes(conversation.inbox_id))
    return false;
  const teamIds = intersectOrSelf(scope.team_ids, filters.teamIds);
  if (teamIds.length && !teamIds.includes(teamIdOf(conversation))) return false;
  if (
    filters.assigneeIds?.length &&
    !filters.assigneeIds.includes(assigneeIdOf(conversation))
  )
    return false;
  if (filters.unassigned && assigneeIdOf(conversation)) return false;
  const labels = (conversation.labels || []).map(lower);
  if ((filters.labels || []).some(l => !labels.includes(lower(l))))
    return false;
  if (
    matcher &&
    filters.extra?.length &&
    !matcher(conversation, withAnd(filters.extra))
  )
    return false;
  const statuses = statusesFor(stage, filters);
  return !statuses.length || statuses.includes(conversation.status);
};

/** Client-side search over loaded cards: contact name or phone. */
export const matchesSearch = (conversation, query) => {
  const q = lower(query).trim();
  if (!q) return true;
  const sender = conversation?.meta?.sender || {};
  const digits = q.replace(/[^\d]/g, '');
  const phone = String(sender.phone_number || '').replace(/[^\d]/g, '');
  return (
    lower(sender.name).includes(q) ||
    lower(sender.email).includes(q) ||
    (digits.length >= 3 && phone.includes(digits))
  );
};

export const wipState = (count, limit) => {
  if (!limit) return 'none';
  if (count > limit) return 'exceeded';
  if (count === limit) return 'full';
  return 'ok';
};

/** Would dropping one more card into this column break its WIP limit? */
export const dropExceedsWip = (stage, currentCount, fromStageId) =>
  !!stage?.wipLimit &&
  fromStageId !== stage.id &&
  currentCount + 1 > stage.wipLimit;

/** Seconds a conversation has been waiting for a reply (0 when not waiting). */
export const waitingSeconds = (conversation, nowSeconds) => {
  const since = Number(conversation?.waiting_since || 0);
  if (!since) return 0;
  return Math.max(0, nowSeconds - since);
};

export const formatWaiting = seconds => {
  if (!seconds) return '';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

export const laneIdOf = (conversation, groupBy) => {
  if (groupBy === GROUP_BY.ASSIGNEE)
    return String(assigneeIdOf(conversation) ?? NO_LANE_ID);
  if (groupBy === GROUP_BY.TEAM)
    return String(teamIdOf(conversation) ?? NO_LANE_ID);
  return NO_LANE_ID;
};

/**
 * Swimlanes: the "none" lane first, then every known agent/team. Lanes with
 * cards come first so a big account's empty lanes sink to the bottom.
 */
export const buildLanes = ({ groupBy, conversations, people, noneName }) => {
  if (groupBy === GROUP_BY.NONE) return [];
  const counts = {};
  conversations.forEach(c => {
    const id = laneIdOf(c, groupBy);
    counts[id] = (counts[id] || 0) + 1;
  });
  const lanes = [
    { id: NO_LANE_ID, name: noneName, thumbnail: '', isNone: true },
    ...people.map(p => ({
      id: String(p.id),
      name: p.name || p.available_name || '',
      thumbnail: p.thumbnail || '',
      isNone: false,
    })),
  ];
  // Cards can reference someone outside the list (e.g. an assistant).
  Object.keys(counts).forEach(id => {
    if (!lanes.some(l => l.id === id)) {
      const sample = conversations.find(c => laneIdOf(c, groupBy) === id);
      const who =
        groupBy === GROUP_BY.TEAM ? sample?.meta?.team : sample?.meta?.assignee;
      lanes.push({
        id,
        name: who?.name || `#${id}`,
        thumbnail: who?.thumbnail || '',
        isNone: false,
      });
    }
  });
  // Stable order (no jumping after a drop): the "none" lane, then lanes with
  // cards by name, then empty lanes by name.
  return lanes
    .map(lane => ({ ...lane, count: counts[lane.id] || 0 }))
    .sort((a, b) => {
      if (a.isNone !== b.isNone) return a.isNone ? -1 : 1;
      if (!a.count !== !b.count) return a.count ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
};

const PRIORITY_RANK = { urgent: 4, high: 3, medium: 2, low: 1 };
const SORT_KEYS = {
  last_activity_at: c => Number(c.last_activity_at || c.timestamp || 0),
  created_at: c => Number(c.created_at || 0),
  priority: c => PRIORITY_RANK[c.priority] || 0,
  // Not waiting sorts last in both directions.
  waiting_since: c => Number(c.waiting_since || 0) || Infinity,
};

/**
 * sales-pipeline.HEADER.3 — the header's sort menu orders the cards inside each
 * column (the filter endpoint always pages by last activity).
 */
export const sortConversations = (conversations, sortBy) => {
  const match = /^(.*)_(asc|desc)$/.exec(sortBy || '');
  const key = match && SORT_KEYS[match[1]];
  if (!key) return conversations;
  const direction = match[2] === 'asc' ? 1 : -1;
  return [...conversations].sort((a, b) => {
    const diff = key(a) - key(b);
    if (!Number.isFinite(diff)) return Number.isFinite(key(a)) ? -1 : 1;
    return diff * direction;
  });
};
